import React, { useEffect, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { money } from '../../../utils/money';

/**
 * Billing → Payments → Failed attempts: every failed checkout and payment of
 * the last N days with its cause in words (Backend
 * billing_service.admin_failed_attempts) — the same "What happened" line the
 * team alert e-mail carries, so the inbox and the panel tell one story.
 *   checkout  Razorpay refused to start the subscription (e.g. a refused offer)
 *   payment   a payment Razorpay reported failed, with its error fields
 *   renewal   a renewal that failed until Razorpay halted the subscription
 *   rejected  a payment whose confirmation failed our signature check
 */

const KIND_LABEL: Record<string, string> = {
  checkout: 'Checkout refused', payment: 'Payment failed', renewal: 'Renewal halted', rejected: 'Not verified',
};

const fmtWhen = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const FailedAttemptsCard: React.FC = () => {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    setRows(null); setErr('');
    apiService.adminBillingFailedAttempts(apiService.getStoredUserId() || '', days).then(r => {
      if (!live) return;
      if (r.success && r.data?.success) setRows(r.data.rows || []);
      else { setErr(r.error || 'Could not load failed attempts'); setRows([]); }
    });
    return () => { live = false; };
  }, [days]);

  return (
    <div className="rounded-xl border border-grayscale-300 bg-white p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
          Failed attempts{rows ? ` (${rows.length})` : ''}
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs">
          {[7, 30, 90, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
      {rows === null ? (
        <div className="text-xs text-grayscale-1100">Loading…</div>
      ) : !rows.length ? (
        <div className="text-xs text-grayscale-1100">No failed attempts in this period.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
              <th className="py-2 pr-3">When</th><th className="pr-3">Who</th><th className="pr-3">What</th>
              <th className="pr-3">Cause</th>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i} className="border-b border-grayscale-200 align-top">
                <td className="py-2 pr-3 whitespace-nowrap text-xs">{fmtWhen(r.at)}</td>
                <td className="py-2 pr-3 text-xs">{r.email || <span className="text-grayscale-1100">unknown</span>}</td>
                <td className="py-2 pr-3 text-xs whitespace-nowrap">
                  <div className="font-medium text-grayscale-1300">{KIND_LABEL[r.kind] || r.kind}</div>
                  <div className="text-grayscale-1100">
                    {[r.plan, r.amount != null ? money(r.amount, r.currency || 'INR') : r.currency,
                      r.code ? `code ${r.code}` : '', r.method].filter(Boolean).join(' · ')}
                  </div>
                  {r.ref && <div className="font-mono text-[10px] text-grayscale-1100">{r.ref}</div>}
                </td>
                <td className="py-2 pr-3 text-xs max-w-[34rem]">
                  <div className="text-grayscale-1300">{r.cause}</div>
                  {r.detail && <div className="text-[11px] text-grayscale-1100 mt-0.5">{r.detail}</div>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FailedAttemptsCard;
